import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlarmClock,
  Bell,
  CalendarOff,
  CheckCheck,
  CheckCircle2,
  ClipboardX,
  FileClock,
  FileSearch,
  FileStack,
  FileText,
  FileX,
  HandCoins,
  Inbox,
  PenLine,
  PrinterX,
  ReceiptText,
  ShieldAlert,
  UserPlus,
  XCircle,
} from 'lucide-react';
import type { NotificationItem } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNotificationsApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';

const POLL_MS = 60_000;

function notificationText(n: NotificationItem): string {
  const p = n.payload;
  const name = String(p.employeeName ?? '');
  const invoice = String(p.invoiceNumber ?? '—');
  switch (n.type) {
    case 'early_payment_request':
      return t('notifications.earlyPaymentRequest', {
        name, invoice, date: String(p.requestedDate ?? ''),
      });
    case 'reimbursement_requested':
      return t('notifications.reimbursementRequested', {
        name, invoice, amount: String(p.amountPln ?? ''),
      });
    case 'reimbursement_approved':
      return t('notifications.reimbursementApproved', {
        invoice, amount: String(p.amountPln ?? ''),
      });
    case 'reimbursement_rejected':
      return t('notifications.reimbursementRejected', {
        invoice, reason: String(p.reason ?? ''),
      });
    case 'my_data_locked':
      return t('notifications.myDataLocked', { name });
    case 'b2b_verification_problem':
      return t(
        p.problem === 'account_not_on_whitelist'
          ? 'notifications.b2bAccountNotOnWhitelist'
          : p.problem === 'verification_unavailable'
            ? 'notifications.b2bVerificationUnavailable'
            : 'notifications.b2bVatStatusNegative',
        { name, contractor: String(p.contractorName ?? name), invoice },
      );
    case 'document_published':
      return t('notifications.documentPublished', { title: String(p.title ?? '') });
    case 'document_acceptance_reminder':
      return p.deadline
        ? t('notifications.documentAcceptanceReminder', {
            title: String(p.title ?? ''), deadline: String(p.deadline),
          })
        : t('notifications.documentAcceptanceReminderNoDeadline', { title: String(p.title ?? '') });
    case 'document_overdue':
      return t('notifications.documentOverdue', {
        employeeName: name, title: String(p.title ?? ''),
      });
    case 'document_returned':
      return p.signerName
        ? t('notifications.documentReturnedSignedBy', {
            title: String(p.title ?? ''), file: String(p.fileName ?? ''), name: String(p.signerName),
          })
        : t('notifications.documentReturned', {
            title: String(p.title ?? ''), file: String(p.fileName ?? ''),
          });
    case 'document_return_superseded':
      return p.signerName
        ? t('notifications.documentReturnSupersededSignedBy', {
            title: String(p.title ?? ''), file: String(p.fileName ?? ''), name: String(p.signerName),
          })
        : t('notifications.documentReturnSuperseded', {
            title: String(p.title ?? ''), file: String(p.fileName ?? ''),
          });
    case 'document_return_needs_review':
      return t('notifications.documentReturnNeedsReview', {
        title: String(p.title ?? ''), file: String(p.fileName ?? ''),
      });
    case 'document_return_failed':
      return t('notifications.documentReturnFailed', {
        title: String(p.title ?? ''), file: String(p.fileName ?? ''),
      });
    case 'document_countersign_pending':
      return t('notifications.documentCountersignPending', {
        title: String(p.title ?? ''),
        number: String(p.number ?? ''),
        representativeName: String(p.representativeName ?? ''),
      });
    case 'document_fully_signed':
      return t('notifications.documentFullySigned', {
        title: String(p.title ?? ''),
        number: String(p.number ?? ''),
        employeeName: name,
      });
    case 'absence_submitted':
      return t(
        p.status === 'approved'
          ? 'notifications.absenceReported'
          : 'notifications.absenceRequested',
        {
          name,
          type: t(`hr.types.${String(p.absenceType ?? '')}`),
          from: String(p.dateFrom ?? ''),
          to: String(p.dateTo ?? ''),
        },
      );
    case 'fiscal_invoice_rejected': {
      const number = String(p.number ?? p.uuid ?? '');
      return p.error
        ? t('notifications.fiscalInvoiceRejectedWithError', { number, error: String(p.error) })
        : t('notifications.fiscalInvoiceRejected', { number });
    }
    case 'fiscal_receipt_rejected': {
      const printer = String(p.printerName ?? '');
      return p.error
        ? t('notifications.fiscalReceiptRejectedWithError', { printer, error: String(p.error) })
        : t('notifications.fiscalReceiptRejected', { printer });
    }
    case 'fiscal_printer_offline':
      return t('notifications.fiscalPrinterOffline', { printer: String(p.printerName ?? '') });
    case 'fiscal_daily_report_failed': {
      const printer = String(p.printerName ?? '');
      const date = String(p.date ?? '');
      return p.error
        ? t('notifications.fiscalDailyReportFailedWithError', { printer, date, error: String(p.error) })
        : t('notifications.fiscalDailyReportFailed', { printer, date });
    }
    case 'fiscal_daily_report_overdue':
      return t('notifications.fiscalDailyReportOverdue', {
        printer: String(p.printerName ?? ''),
        date: String(p.missedDay ?? ''),
      });
    case 'crm_lead_received': {
      // displayName already falls back to companyName when there is no person
      // name — only mention the company separately when it adds information.
      const leadName = String(p.displayName ?? '');
      const company = p.companyName ? String(p.companyName) : '';
      const source = String(p.sourceName ?? '');
      return company && company !== leadName
        ? t('notifications.crmLeadReceivedCompany', { name: leadName, company, source })
        : t('notifications.crmLeadReceived', { name: leadName, source });
    }
    case 'contractor_document_expiring': {
      const label = String(p.label ?? '');
      const contractor = p.contractorName ? String(p.contractorName) : t('notifications.unknownContractor');
      const daysLeft = Number(p.daysLeft ?? 0);
      if (daysLeft > 0) {
        return daysLeft === 1
          ? t('notifications.contractorDocumentExpiringOne', { label, contractor })
          : t('notifications.contractorDocumentExpiringSoon', { label, contractor, days: daysLeft });
      }
      if (daysLeft === 0) {
        return t('notifications.contractorDocumentExpiringToday', { label, contractor });
      }
      return Math.abs(daysLeft) === 1
        ? t('notifications.contractorDocumentExpiredOne', { label, contractor })
        : t('notifications.contractorDocumentExpired', { label, contractor, days: Math.abs(daysLeft) });
    }
    default:
      if (p.invoiceNumber) {
        return t('notifications.fundsNotReceivedInvoice', { name, invoice });
      }
      return t('notifications.fundsNotReceived', { name });
  }
}

function notificationIcon(n: NotificationItem) {
  switch (n.type) {
    case 'early_payment_request':
      return <HandCoins className="size-4 shrink-0 text-indigo-600 dark:text-indigo-400" />;
    case 'reimbursement_requested':
      return <ReceiptText className="size-4 shrink-0 text-indigo-600 dark:text-indigo-400" />;
    case 'reimbursement_approved':
      return <CheckCircle2 className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />;
    case 'reimbursement_rejected':
      return <XCircle className="size-4 shrink-0 text-rose-600 dark:text-rose-400" />;
    case 'my_data_locked':
      return <ShieldAlert className="size-4 shrink-0 text-rose-600 dark:text-rose-400" />;
    case 'b2b_verification_problem':
      return <ShieldAlert className="size-4 shrink-0 text-amber-600 dark:text-amber-500" />;
    case 'document_published':
      return <FileText className="size-4 shrink-0 text-indigo-600 dark:text-indigo-400" />;
    case 'document_acceptance_reminder':
      return <AlarmClock className="size-4 shrink-0 text-amber-600 dark:text-amber-500" />;
    case 'document_overdue':
      return <ShieldAlert className="size-4 shrink-0 text-rose-600 dark:text-rose-400" />;
    case 'document_returned':
      return <Inbox className="size-4 shrink-0 text-indigo-600 dark:text-indigo-400" />;
    case 'document_return_superseded':
      return <FileStack className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />;
    case 'document_return_needs_review':
      return <FileSearch className="size-4 shrink-0 text-amber-600 dark:text-amber-500" />;
    case 'document_return_failed':
      return <FileX className="size-4 shrink-0 text-rose-600 dark:text-rose-400" />;
    case 'document_countersign_pending':
      return <PenLine className="size-4 shrink-0 text-amber-600 dark:text-amber-500" />;
    case 'document_fully_signed':
      return <CheckCheck className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />;
    case 'absence_submitted':
      return <CalendarOff className="size-4 shrink-0 text-indigo-600 dark:text-indigo-400" />;
    case 'fiscal_invoice_rejected':
      return <FileX className="size-4 shrink-0 text-rose-600 dark:text-rose-400" />;
    case 'fiscal_receipt_rejected':
      return <ReceiptText className="size-4 shrink-0 text-rose-600 dark:text-rose-400" />;
    case 'fiscal_printer_offline':
      return <PrinterX className="size-4 shrink-0 text-amber-600 dark:text-amber-500" />;
    case 'fiscal_daily_report_failed':
    case 'fiscal_daily_report_overdue':
      return <ClipboardX className="size-4 shrink-0 text-rose-600 dark:text-rose-400" />;
    case 'crm_lead_received':
      return <UserPlus className="size-4 shrink-0 text-indigo-600 dark:text-indigo-400" />;
    case 'contractor_document_expiring':
      // Etap, nie liczba dni: 'expired' to i dzień ważności, i każdy dzień po.
      return n.payload.stage === 'expired' ? (
        <FileX className="size-4 shrink-0 text-rose-600 dark:text-rose-400" />
      ) : (
        <FileClock className="size-4 shrink-0 text-amber-600 dark:text-amber-500" />
      );
    default:
      return <AlarmClock className="size-4 shrink-0 text-rose-600 dark:text-rose-400" />;
  }
}

function relativeTime(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return t('notifications.justNow');
  if (minutes < 60) return t('notifications.minutesAgo', { m: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('notifications.hoursAgo', { h: hours });
  return new Date(iso).toLocaleDateString();
}

/** Navbar bell: unread badge (60 s polling) + popover list of the caller's notifications. */
export function NotificationBell({ nip }: { nip: string }) {
  const { listNotifications, markNotificationsRead } = useNotificationsApi();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await listNotifications(nip);
      setItems(res.items);
      setUnread(res.unreadCount);
    } catch {
      // polling — stay quiet, next tick may succeed
    }
  }, [nip, listNotifications]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  const handleClick = async (n: NotificationItem) => {
    setOpen(false);
    if (!n.readAt) {
      markNotificationsRead(nip, { ids: [n.id] }).then(refresh).catch(() => undefined);
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    try {
      await markNotificationsRead(nip, { all: true });
      await refresh();
    } catch {
      // toast-free: the bell will simply keep the badge
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative size-8" aria-label={t('notifications.title')}>
          <Bell className="size-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">{t('notifications.title')}</span>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleMarkAll}>
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-muted-foreground px-3 py-6 text-center text-sm">{t('notifications.empty')}</p>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleClick(n)}
                    className={cn(
                      'hover:bg-muted/60 flex w-full items-start gap-2.5 px-3 py-2.5 text-left',
                      !n.readAt && 'bg-muted/30',
                    )}
                  >
                    <span className="mt-0.5">{notificationIcon(n)}</span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block text-sm', !n.readAt && 'font-medium')}>
                        {notificationText(n)}
                      </span>
                      <span className="text-muted-foreground block text-xs">{relativeTime(n.createdAt)}</span>
                    </span>
                    {!n.readAt && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-indigo-500" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
